import React from 'react';
import styled from 'styled-components';

import { CtaButton } from '@ui/buttons/Button.tsx';
import { Icon } from '@ui/icons/Icon.tsx';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';
import { handleKeyboardSelect } from '../../../../utils/keyboardUtils.ts';

const ButtonWrapper = styled.div`
  display: flex;
  margin: 12px 0;

  ${Icon} {
    margin-inline-end: 8px;
  }
`;

const CtaButtonStyled = styled(CtaButton)`
  border-radius: ${({ theme }) => theme.standardRadius};

  &:focus-visible {
    outline: 1px solid blue;
    outline-offset: 2px;
  }
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
`;

/**
 * Renders a "Reserve Parking" CTA for parking POIs by grabbing the first parking-type link.

 * Props:
 *  - poi.links: array containing a link with type "parking" and its booking URL
 *  - poi.category: must start with "parking" to show this button
 *  - T: translation function
 *  - onSubmit: reports the click to analytics2 and App Insights (poiView/reserveParkingClick)
 */
const ReserveParkingButton = ({ poi, T, onSubmit }) => {
  const colors = useCSSColors();
  const bookingLink = Array.isArray(poi.links) ? poi.links.find(link => link?.type === 'parking')?.url : undefined;
  const { category } = poi;
  const isParking = typeof category === 'string' && category.startsWith('parking');
  if (!isParking || !bookingLink) {
    return null;
  }

  const handleClick = e => {
    e.stopPropagation();
    // Report the click before navigating so the event is recorded even if the popup is blocked.
    onSubmit?.(poi, bookingLink);
    window.open(bookingLink, '_blank');
  };

  return (
    <ButtonWrapper data-testid="ReserveParkingButton">
      <CtaButtonStyled
        onClick={handleClick}
        onKeyDown={handleKeyboardSelect(handleClick, { stopPropagation: true })}
        backgroundColor={colors.primaryButton}
        hoverBackgroundColor={colors.primaryButtonHover}
        fontColor={colors.primaryButtonText}
        tabIndex={0}
        aria-label={T('poiView:Reserve Parking')}
      >
        <IconWrapper>
          <Icon cdn name="calendar" width={24} height={24} fillColor={colors.primaryButtonText} />
        </IconWrapper>
        {T('poiView:Reserve Parking')}
      </CtaButtonStyled>
    </ButtonWrapper>
  );
};

export default ReserveParkingButton;
